import { createSlice } from "@reduxjs/toolkit";
import { message } from "antd";
import {
  fetchAggregatedSearches,
  fetchSearchEvents,
  exportSearchAnalytics,
} from "./searchAnalyticsThunk";

const initialState = {
  aggregated: {
    data: [],
    pagination: {},
    loading: false,
    error: null,
  },
  raw: {
    data: [],
    pagination: {},
    loading: false,
    error: null,
  },
  filters: {
    product: "all",
    userType: "all",
    from: null,
    to: null,
    includeBots: false,
    search: "",
    datePreset: "all",
  },
  sort: {
    field: "occurrenceCount",
    order: "desc",
  },
  page: 1,
  pageSize: 10,
  activeTab: "aggregated",
  exportLoading: false,
};

const searchAnalyticsSlice = createSlice({
  name: "searchAnalytics",
  initialState,
  reducers: {
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.page = 1;
    },
    setSort: (state, action) => {
      state.sort = action.payload;
      state.page = 1;
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
      state.page = 1;
      state.sort = initialState.sort;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAggregatedSearches.pending, (state) => {
        state.aggregated.loading = true;
        state.aggregated.error = null;
      })
      .addCase(fetchAggregatedSearches.fulfilled, (state, action) => {
        const payload = action.payload || {};
        state.aggregated.loading = false;
        state.aggregated.data = payload.data || payload.items || [];
        state.aggregated.pagination = payload.pagination || {};
      })
      .addCase(fetchAggregatedSearches.rejected, (state, action) => {
        state.aggregated.loading = false;
        state.aggregated.error = action.payload?.message || "Failed to load aggregated searches";
        message.error(state.aggregated.error);
      })
      .addCase(fetchSearchEvents.pending, (state) => {
        state.raw.loading = true;
        state.raw.error = null;
      })
      .addCase(fetchSearchEvents.fulfilled, (state, action) => {
        const payload = action.payload || {};
        state.raw.loading = false;
        state.raw.data = payload.data || payload.items || [];
        state.raw.pagination = payload.pagination || {};
      })
      .addCase(fetchSearchEvents.rejected, (state, action) => {
        state.raw.loading = false;
        state.raw.error = action.payload?.message || "Failed to load search log";
        message.error(state.raw.error);
      })
      .addCase(exportSearchAnalytics.pending, (state) => {
        state.exportLoading = true;
      })
      .addCase(exportSearchAnalytics.fulfilled, (state, action) => {
        state.exportLoading = false;
        const response = action.payload;
        const disposition = response?.headers?.["content-disposition"] || "";
        const filenameMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i) || disposition.match(/filename="?([^";]+)"?/i);
        const filename = filenameMatch ? decodeURIComponent(filenameMatch[1]) : "search-analytics.csv";
        const blob = new Blob([response.data], { type: response.headers["content-type"] || "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        message.success("Search export downloaded");
      })
      .addCase(exportSearchAnalytics.rejected, (state, action) => {
        state.exportLoading = false;
        const messageText = action.payload?.message || "Failed to export search data";
        message.error(messageText);
      });
  },
});

export const {
  setActiveTab,
  setPage,
  setPageSize,
  setFilters,
  setSort,
  resetFilters,
} = searchAnalyticsSlice.actions;

export default searchAnalyticsSlice.reducer;
